import { createFileRoute } from "@tanstack/react-router";
import { CircleHelp } from "lucide-react";
import { TooltipContent } from "@radix-ui/react-tooltip";
import { ProvenanceTemplate, VideoTutorialBox } from "./-components";
import type {
  ProvenanceV2EntryBase,
  WritingAnalysisResult,
} from "@/lib/types/provenance";
import { writingAnalysis } from "@/lib/textAnalyze";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export const Route = createFileRoute("/_app/_provenance/writing-analysis")({
  component: RouteComponent,
  staticData: {
    title: "Writing Analysis",
  },
  head: () => ({
    meta: [
      {
        name: "description",
        content:
          "Get comprehensive feedback on grammar, tone, and AI-generated patterns with Polygraf AI’s Writing Analysis - optimize readability and compliance, deployable in local or on-premises environments.",
      },
    ],
  }),
});

function RouteComponent() {
  const { title } = Route.useMatch({
    select: (m) => m.staticData,
  });
  return (
    <div className="relative">
      <VideoTutorialBox
        title="New to Polygraf?"
        description={`Learn how to use \n Writing Analysis`}
        youtubeUrl="https://youtu.be/sl85RxHymTo"
      />
      <ProvenanceTemplate
        title={title!}
        subtitle="Analyze text quality and statistics."
        model="WRITING_ANALYSIS"
        analyze={(text) => writingAnalysis(text)}
        SuccessSection={WritingAnalysisPercentages}
        __addHumanizeButton
      />
    </div>
  );
}

type WritingAnalysisFields = Omit<WritingAnalysisResult, "average_word_length">;

const writingAnalysisNameMap = {
  readability: {
    label: "Readability",
    description:
      "Measures how easy it is to understand the text, with higher scores indicating more complex writing.",
  },
  syntactic_tree_depth: {
    label: "Syntactic Tree Depth",
    description:
      "Indicates the complexity of sentence structure, with higher values showing more complex constructions.",
  },
  vocabulary_richness: {
    label: "Vocabulary Richness",
    description:
      "Reflects the diversity of words used, where higher percentages indicate a greater variety of vocabulary.",
  },
  perplexity: {
    label: "Perplexity",
    description:
      "Assesses the predictability of the text, with lower values imply easier prediction and may hint at AI generation.",
  },
  average_sentence_length: {
    label: "Average Sentence Length",
    description:
      "Shows the typical number of words per sentence, with longer sentences possibly indicating more complex ideas.",
  },
  cognitive_flow_index: {
    label: "Cognitive Flow Index",
    description:
      "Measures how natural and creative a piece of writing feels, with higher scores (closer to 100) indicating more dynamic, engaging, and human-like text.",
  },
} as const satisfies Record<
  keyof WritingAnalysisFields,
  { label: string; description: string }
>;

function WritingAnalysisPercentages({
  entry: { result },
}: {
  entry: ProvenanceV2EntryBase<"WRITING_ANALYSIS", string>;
}) {
  return (
    <>
      <div className="mt-4 mb-4 h-[2px] bg-gray-200 dark:bg-gray-800"></div>
      <Card className="space-y-6 rounded-3xl p-6 shadow-sm">
        {/* Metrics */}
        <div className="space-y-6">
          {Object.entries(writingAnalysisNameMap).map(
            ([feature, { label, description }]) => {
              const percentage = result[feature as keyof WritingAnalysisFields];
              return (
                <div key={label} className="space-y-2">
                  <div className="flex items-center gap-2">
                    <h3 className="text-xl font-medium dark:text-white">
                      {label}
                    </h3>
                    <TooltipProvider
                      delayDuration={500}
                      skipDelayDuration={500}
                    >
                      <Tooltip>
                        <TooltipTrigger className="cursor-help">
                          <CircleHelp className="size-5" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <Card>
                            <CardContent className="text-sm">
                              {description}
                            </CardContent>
                          </Card>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <div className="flex items-center gap-4">
                    <Progress
                      className="bg-gray-200 dark:bg-gray-800"
                      indicatorClassName="bg-gray-700 animate-in slide-in-from-left duration-750"
                      value={percentage}
                    />
                    {percentage.toFixed(1)}%
                  </div>
                </div>
              );
            },
          )}
        </div>
      </Card>
    </>
  );
}
